/**
 * Tenant Query Hooks
 * 封裝租戶（商家）相關的查詢 hooks（使用 SWR）
 */

import { useTenantsGetMe } from '../../api/posClient';
import { swrDetailConfig } from '../../lib/swr-config';
import type { SWRConfiguration } from 'swr';

/** SWR 選項（含 SDK 擴充的 enabled / swrKey） */
type SWROptionsWithEnabled = SWRConfiguration & {
  enabled?: boolean;
  swrKey?: string | null;
};

/**
 * 取得當前商家資訊
 */
export function useTenant(options?: {
  fetch?: RequestInit;
  /**
   * 是否啟用查詢（預設：根據 token 自動判斷）
   */
  enabled?: boolean;
  /**
   * 是否在視窗焦點時重新獲取資料（預設：true，確保商家資訊最新）
   */
  revalidateOnFocus?: boolean;
  /**
   * 去重複請求間隔（毫秒，預設：5 秒）
   */
  dedupingInterval?: number;
  // 允許傳入其他 SWR 選項（含 SDK 的 enabled、swrKey）
  swr?: SWROptionsWithEnabled;
}) {
  const {
    enabled,
    revalidateOnFocus = true,
    dedupingInterval = 60000, // 60 秒，減少多組件同時 mount 時重複請求 tenants/me
    swr: swrOptions,
  } = options || {};

  const result = useTenantsGetMe({
    fetch: options?.fetch,
    swr: {
      ...swrDetailConfig,
      enabled,
      revalidateOnFocus,
      dedupingInterval,
      ...swrOptions,
    },
  });

  // 提取實際資料：SDK 回傳 result.data = { data: parsedBody, status, headers }，
  // parsedBody = API body = { success, data: tenant }，故 tenant 在 result.data.data.data。
  // 相容：有 status 包裝時取 inner.data；或 result.data 即 API body（無 status）時取 data / 或即 tenant。
  const raw = result.data;
  const hasStatusWrapper =
    raw != null &&
    typeof raw === 'object' &&
    'status' in (raw as object) &&
    (raw as { status: number }).status === 200;
  const apiBody =
    hasStatusWrapper && raw != null && 'data' in (raw as object)
      ? (raw as { data: unknown }).data
      : raw;
  const tenantFromBody =
    apiBody != null &&
    typeof apiBody === 'object' &&
    'data' in (apiBody as object) &&
    (apiBody as { data?: unknown }).data != null
      ? (apiBody as { data: unknown }).data
      : undefined;
  const tenantDirect =
    apiBody != null &&
    typeof apiBody === 'object' &&
    'id' in (apiBody as object) &&
    'name' in (apiBody as object)
      ? apiBody
      : undefined;
  const tenant = (tenantFromBody ?? tenantDirect) as
    | { id: string; name?: string; phone?: string | null; [k: string]: unknown }
    | undefined;

  return {
    tenant,
    isLoading: result.isLoading,
    isError: !!result.error,
    error: result.error,
    mutate: result.mutate,
    isValidating: result.isValidating,
  };
}
